import Input from '../controls/Input';
import React, { useEffect, useRef, useState } from 'react'
import MasterPage from '../app/MasterPage';
import Card from '../controls/Card';
import { MdErrorOutline, MdMedicalInformation, MdOutlineLocalPhone, MdOutlineVerifiedUser } from "react-icons/md";
import { PiPassword, PiPhone } from "react-icons/pi";
import { BsGenderAmbiguous, BsShieldCheck } from "react-icons/bs";
import { RiLockPasswordFill, RiFileList3Line } from 'react-icons/ri';
import { FaAddressCard, FaBriefcaseMedical, FaLocationDot, FaMapLocationDot, FaUserDoctor, FaStethoscope } from "react-icons/fa6";
import { FaUserGraduate } from "react-icons/fa";
import { IoClose, IoCheckmarkDoneCircleOutline } from 'react-icons/io5';
import { CreateNewUser, MobileOtpGenerate, MobileOtpVerify } from '../../configurations/crud/mobileVerificationCrud';
import { useToast } from '../controls/Toast/ToastService';
import { useFormik } from 'formik';
import * as yup from 'yup'
import LodingButton from '../controls/LodingButton';
import { GetAll } from '../../configurations/crud/stateCrud';
import * as cityCrud from '../../configurations/crud/cityCrud'
import * as getSpecialisation from '../../configurations/crud/doctorsSpecialisation'
import { GetEGender } from '../../configurations/enums/EGender';
import UserRegisterCompleted from './UserRegisterCompleted';
import { MyCaptcha, validateCaptcha } from '../base/MyCaptcha';
import { useNavigate } from 'react-router-dom';
import Select from '../controls/Select';
import useGlobalLoader from '../../configurations/useGlobalLoader';

const schema = yup.object().shape({
    fullName: yup.string().required("Full Name is required"),
    password: yup.string().required("Password is required.").matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/,
        "Must be 8 Chars, One Upper, Lower, Number & Special Char"
    ),
    specialisationId: yup.string().required("Specialisation is required"),
    degree: yup.string().required("Degree is required"),
    experiance: yup.string().required("Experience is required"),
    address: yup.string().required("Address is required"),
    cityId: yup.string().required("City is required"),
    stateId: yup.string().required("State is required"),
})

/* ------------------------------------------------------------------ */
/*  Terms & Conditions content — based on Doctora (Digital OPD)        */
/* ------------------------------------------------------------------ */
const TERMS = [
    {
        title: "1. About Doctora – Digital OPD",
        body: "Doctora (doctora.live) is a Digital OPD platform designed to connect patients and doctors, manage schedules, and simplify healthcare. It enables online appointment booking, patient record management, secure messaging, video consultations, digital prescriptions, billing and reporting for individual practitioners as well as multi-specialty hospitals."
    },
    {
        title: "2. Doctor Eligibility & Verification",
        body: "By registering as a doctor, you confirm that you are a qualified and registered medical practitioner under the applicable medical council (e.g. NMC/State Medical Council in India). You agree that the details provided — name, specialisation, degree, and experience — are true and accurate. Doctora reserves the right to verify your credentials and suspend or remove accounts with false or misleading information."
    },
    {
        title: "3. Account & Security",
        body: "You are responsible for maintaining the confidentiality of your password, security PIN and OTP. Any activity performed through your account will be considered as performed by you. Do not share your login credentials with anyone. Notify us immediately at info@doctora.live if you suspect unauthorized access."
    },
    {
        title: "4. Use of the Platform",
        body: "The platform must be used only for legitimate healthcare purposes — managing OPD schedules, patient appointments, consultations, prescriptions and records. Misuse of patient data, spamming, or any unlawful activity is strictly prohibited and may lead to permanent account termination."
    },
    {
        title: "5. Patient Data & Confidentiality",
        body: "As a doctor, you agree to maintain the confidentiality of all patient information accessed through Doctora, in line with applicable medical ethics and data protection laws. Patient records, prescriptions and communication must not be shared with unauthorized third parties."
    },
    {
        title: "6. Telemedicine Disclaimer",
        body: "Online/video consultations conducted through Doctora are subject to the Telemedicine Practice Guidelines. You acknowledge that telemedicine cannot replace an in-person examination in every case, and you agree to advise in-person visits or emergency care whenever clinically required."
    },
    {
        title: "7. Fees, Billing & Prescriptions",
        body: "Digital prescriptions, billing and reports generated through the platform are your professional responsibility. You agree to issue prescriptions only after proper consultation and in accordance with applicable laws and medical council guidelines."
    },
    {
        title: "8. Communication Consent",
        body: "By registering, you consent to receive OTPs, appointment alerts, automated reminders and service-related communication from Doctora via SMS, phone, or other digital channels on the mobile number provided."
    },
    {
        title: "9. Limitation of Liability",
        body: "Doctora is a technology platform that facilitates connection between doctors and patients. Doctora is not responsible for the medical advice, diagnosis or treatment provided by doctors. All clinical decisions and their outcomes remain the sole responsibility of the treating doctor."
    },
    {
        title: "10. Changes to Terms",
        body: "Doctora may update these Terms & Conditions from time to time. Continued use of the platform after changes are published constitutes your acceptance of the revised terms. For any queries, contact info@doctora.live or +91 95599 52123."
    },
]

/* ------------------------------------------------------------------ */
/*  Step indicator                                                     */
/* ------------------------------------------------------------------ */
const STEPS = [
    { id: 1, label: "Mobile", Icon: MdOutlineLocalPhone },
    { id: 2, label: "Verify OTP", Icon: BsShieldCheck },
    { id: 3, label: "Your Details", Icon: FaUserDoctor },
]

function Stepper({ level }) {
    return (
        <div className="flex items-center justify-center w-full max-w-md mx-auto mb-6 px-2">
            {STEPS.map((step, i) => {
                const done = level > step.id;
                const active = level === step.id;
                return (
                    <React.Fragment key={step.id}>
                        <div className="flex flex-col items-center gap-1 shrink-0">
                            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-base sm:text-lg transition-all duration-300 border-2
                                ${done ? "bg-sky-500 border-sky-500 text-white"
                                    : active ? "bg-white border-sky-500 text-sky-500 shadow-[0_0_0_4px_rgba(47,180,255,0.2)]"
                                        : "bg-white border-gray-300 text-gray-400"}`}>
                                {done ? <IoCheckmarkDoneCircleOutline /> : <step.Icon />}
                            </div>
                            <span className={`text-[10px] sm:text-xs font-medium ${active || done ? "text-sky-600" : "text-gray-400"}`}>
                                {step.label}
                            </span>
                        </div>
                        {i < STEPS.length - 1 &&
                            <div className={`flex-1 h-0.5 mx-1 sm:mx-2 mb-4 rounded transition-all duration-500 ${level > step.id ? "bg-sky-500" : "bg-gray-200"}`} />
                        }
                    </React.Fragment>
                )
            })}
        </div>
    )
}

/* ------------------------------------------------------------------ */
/*  Terms & Conditions Modal — must scroll to bottom to agree          */
/* ------------------------------------------------------------------ */
function TermsModal({ open, onClose, onAgree }) {
    const [readCompleted, setReadCompleted] = useState(false)
    const scrollRef = useRef(null)

    useEffect(() => {
        if (open) {
            setReadCompleted(false)
            // If content is shorter than the box (large screens), mark as read
            setTimeout(() => {
                const el = scrollRef.current
                if (el && el.scrollHeight <= el.clientHeight + 10) setReadCompleted(true)
            }, 100)
        }
    }, [open])

    const onScroll = () => {
        const el = scrollRef.current
        if (!el) return
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
            setReadCompleted(true)
        }
    }

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
            {/* backdrop */}
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-[fadeIn_.25s_ease]">
                {/* header */}
                <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 bg-gradient-to-r from-sky-500 to-cyan-400 text-white">
                    <div className="flex items-center gap-2">
                        <RiFileList3Line className="text-2xl shrink-0" />
                        <div>
                            <h2 className="font-semibold text-base sm:text-lg leading-tight">Terms & Conditions</h2>
                            <p className="text-[11px] sm:text-xs text-sky-100">Doctora — Digital OPD (doctora.live)</p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close"
                        className="p-1.5 rounded-full hover:bg-white/20 transition text-xl">
                        <IoClose />
                    </button>
                </div>

                {/* scrollable body */}
                <div ref={scrollRef} onScroll={onScroll}
                    className="overflow-y-auto px-4 sm:px-6 py-4 space-y-4 text-sm text-gray-700 grow">
                    <p className="text-gray-600 italic">
                        Please read the following terms carefully. You must scroll to the end before you can accept.
                    </p>
                    {TERMS.map((t) => (
                        <div key={t.title}>
                            <h3 className="font-semibold text-gray-900 mb-1">{t.title}</h3>
                            <p className="leading-relaxed text-justify">{t.body}</p>
                        </div>
                    ))}
                    <div className="pt-2 pb-1 text-center text-xs text-gray-400">— End of Terms & Conditions —</div>
                </div>

                {/* footer */}
                <div className="border-t border-gray-100 px-4 sm:px-6 py-4 bg-gray-50">
                    {!readCompleted &&
                        <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                            <MdErrorOutline className="shrink-0" /> Scroll down and read the complete terms to enable the button.
                        </p>
                    }
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                        <button onClick={onClose}
                            className="px-4 py-2.5 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-100 transition">
                            Cancel
                        </button>
                        <button onClick={() => { onAgree(); onClose(); }} disabled={!readCompleted}
                            className={`px-5 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition
                                ${readCompleted
                                    ? "bg-sky-500 hover:bg-sky-600 text-white shadow-md shadow-sky-200"
                                    : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
                            <MdOutlineVerifiedUser className="text-lg" />
                            I have read & I agree
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */
export default function CreateAccount() {
    const { start, end } = useGlobalLoader()
    const [level, setLevel] = useState(1)
    const [lodingCrud, setLodingCrud] = useState(false);
    const [mobileNumber, setMobileNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [token, setToken] = useState('');
    const toast = useToast()
    const [lodingStateCrud, setLodingStateCrud] = useState(false)
    const [stateData, setStateData] = useState([])
    const [lodingCityCrud, setLodingCityCrud] = useState(false)
    const [cityData, setCityData] = useState([]);
    const [specialisationData, setSpecialisationData] = useState([])
    const [loding, setLoding] = useState(false)
    const [ctext, setCtext] = useState("");
    const [errorCount, setErrorCount] = useState(0);
    const navigate = useNavigate()

    // Terms & Conditions
    const [showTerms, setShowTerms] = useState(false)
    const [agreedTerms, setAgreedTerms] = useState(false)

    const { values, handleBlur, handleChange, handleSubmit, errors, touched, isValid, setValues } = useFormik({
        initialValues: {
            fullName: "",
            password: "",
            specialisationId: "",
            degree: "",
            securityPIN: "0000",
            experiance: "",
            gender: "0",
            address: "",
            cityId: "",
            stateId: ""
        },
        validationSchema: schema,
        onSubmit(val) {
            if (!agreedTerms) {
                toast.open("Please read & accept the Terms and Conditions first.", <MdErrorOutline />, "danger")
                setShowTerms(true)
                return
            }
            CreateAccount(val)
        }
    })

    const SendOtpMobile = () => {
        if (mobileNumber) {
            if (mobileNumber.length !== 10) {
                toast.open("Please enter a valid 10 digit mobile number", <MdErrorOutline />, "danger")
                return
            }
            let flag = validateCaptcha(ctext)
            if (ctext && flag) {
                setLodingCrud(true)
                start()
                MobileOtpGenerate(mobileNumber).then(res => {
                    let mdata = res.data
                    if (mdata.result.succeeded) {
                        setLevel(2);
                        toast.open("Otp Send Successfully.", <IoCheckmarkDoneCircleOutline />, "success")
                    } else {
                        toast.open(mdata.result.message, <MdErrorOutline />, "danger")
                    }
                    setLodingCrud(false)
                    end()
                }).catch(() => {
                    setLodingCrud(false)
                    end()
                    toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000)
                })
            }
            else {
                if (errorCount < 2) {
                    setErrorCount(pre => pre + 1)
                    toast.open("Invalid Captcha", <MdErrorOutline />, "danger")
                }
                else {
                    navigate("/")
                    toast.open("Too many try.", <MdErrorOutline />, "danger")
                }
            }
        }
        else {
            toast.open("Please enter mobile number", <MdErrorOutline />, "danger")
        }
    }

    const VerifyOtpMobile = () => {
        if (otp) {
            setLodingCrud(true)
            start()
            MobileOtpVerify(mobileNumber, otp).then(res => {
                let mdata = res.data
                if (mdata.succeeded) {
                    setLevel(3);
                    setToken(mdata.message)
                    toast.open("Otp Verifed Successfully.", <IoCheckmarkDoneCircleOutline />, "success")
                } else {
                    toast.open("Invalid OTP", <MdErrorOutline />, "danger")
                }
                setLodingCrud(false)
                end()
            }).catch(() => {
                setLodingCrud(false)
                end()
                toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000)
            })
        }
        else {
            toast.open("Please enter OTP", <MdErrorOutline />, "danger")
        }
    }

    const CreateAccount = (param) => {
        setLodingCrud(true)
        start()
        let mparam = {
            fullName: param.fullName,
            mobileNo: mobileNumber,
            password: param.password,
            token: token,
            specialisationId: param.specialisationId,
            degree: param.degree,
            securityPIN: param.securityPIN,
            experiance: param.experiance,
            gender: param.gender,
            address: param.address,
            cityId: param.cityId,
            stateId: param.stateId
        }
        CreateNewUser(mparam).then(res => {
            let mdata = res.data
            if (mdata.succeeded) {
                setValues({
                    fullName: "",
                    password: "",
                    specialisationId: "",
                    degree: "",
                    securityPIN: "0000",
                    experiance: "",
                    gender: "0",
                    address: "",
                    cityId: "",
                    stateId: ""
                })
                setLevel(4);
                toast.open("Account Created Successfully.", <IoCheckmarkDoneCircleOutline />, "success")
            } else {
                toast.open(mdata.message, <MdErrorOutline />, "danger")
            }
            setLodingCrud(false)
            end()
        }).catch(() => {
            setLodingCrud(false)
            end()
            toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000)
        })
    }

    useEffect(() => {
        GetStates()
        GetSpecialisations()
    }, [])

    const GetStates = () => {
        setLodingStateCrud(true)
        start()
        GetAll().then(res => {
            let mdata = res.data
            if (mdata.result.succeeded) {
                setStateData(mdata.data)
            } else {
                setStateData([])
                toast.open(mdata.result.message, <MdErrorOutline />, "danger")
            }
            setLodingStateCrud(false)
            end()
        }).catch(() => {
            setStateData([])
            setLodingStateCrud(false)
            end()
            toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000)
        })
    }

    useEffect(() => {
        if (stateData.length > 0 && values.stateId != "") {
            GetCities()
        }
        setValues({ ...values, cityId: "" })
    }, [values.stateId])

    const GetCities = () => {
        setLodingCityCrud(true)
        start()
        cityCrud.GetAll(values.stateId).then(res => {
            let mdata = res.data
            if (mdata.result.succeeded) {
                setCityData(mdata.data)
            } else {
                setCityData([])
                toast.open(mdata.result.message, <MdErrorOutline />, "danger")
            }
            setLodingCityCrud(false)
            end()
        }).catch(() => {
            setCityData([])
            setLodingCityCrud(false)
            end()
            toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000)
        })
    }

    const GetSpecialisations = () => {
        setLoding(true)
        start()
        getSpecialisation.GetAll().then(res => {
            let mdata = res.data
            if (mdata.result.succeeded) {
                setSpecialisationData(mdata.data)
            } else {
                setSpecialisationData([])
                toast.open(mdata.result.message, <MdErrorOutline />, "danger")
            }
            setLoding(false)
            end()
        }).catch(() => {
            setSpecialisationData([])
            setLoding(false)
            end()
            toast.open("Internet not connected, please connect and refresh", <MdErrorOutline />, "danger", 5000)
        })
    }

    const onMobileChange = (event) => {
        const numericValue = event.target.value.replace(/[^0-9]/g, '');
        setMobileNumber(numericValue);
    };

    /* checkbox click handler: force reading before agreeing */
    const onTermsCheckboxClick = (e) => {
        e.preventDefault()
        if (agreedTerms) {
            setAgreedTerms(false)   // allow un-checking
        } else {
            setShowTerms(true)      // must read first — open modal
        }
    }

    /* small reusable header block for step 1 & 2 cards */
    const BrandHeader = ({ subtitle }) => (
        <div className="text-center mb-2">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center text-white text-3xl shadow-lg shadow-sky-200 mb-3">
                <FaStethoscope />
            </div>
            <h1 className="text-xl font-bold text-gray-800">Doctor Registration</h1>
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
        </div>
    )

    return (
        <MasterPage>
            <TermsModal
                open={showTerms}
                onClose={() => setShowTerms(false)}
                onAgree={() => setAgreedTerms(true)}
            />

            {/* ---------------- LEVEL 1 : Mobile number ---------------- */}
            {level === 1 &&
                <div className="flex flex-col min-h-full justify-center px-4 sm:px-6 py-10 lg:px-8 bg-gradient-to-b from-sky-50 via-slate-50 to-slate-100">
                    <Stepper level={level} />
                    <div className="mx-auto w-full max-w-sm">
                        <Card title={"Create An Account"} isBorder={true} isBack={true}>
                            <div className="my-5 sm:mx-auto sm:w-full sm:max-w-sm">
                                <div className="space-y-6">
                                    <BrandHeader subtitle="Digital OPD — connect with patients, manage schedules & simplify healthcare" />
                                    <div>
                                        <label htmlFor="mobileNumber" className="block text-sm font-medium leading-6 text-gray-900">
                                            Mobile no.
                                        </label>
                                        <div className="mt-2">
                                            <Input placeholder="Enter 10 digit mobile no."
                                                id="mobileNumber"
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={10}
                                                value={mobileNumber}
                                                onChange={onMobileChange}
                                                Icon={<MdOutlineLocalPhone className="text-sky-600 text-lg" />} />
                                        </div>
                                        <div className="mt-3">
                                            <label className="block clearfix" style={{ textAlign: "right" }}>
                                                <MyCaptcha setResult={setCtext} />
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        {lodingCrud ?
                                            <LodingButton text={"Sending otp.."} />
                                            :
                                            <button className="btn-blue w-full" onClick={SendOtpMobile}>
                                                Get OTP
                                            </button>}
                                        <p className="text-[11px] text-gray-400 text-center mt-3">
                                            An OTP will be sent to this number for verification.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            }

            {/* ---------------- LEVEL 2 : OTP verification ---------------- */}
            {level === 2 &&
                <div className="flex flex-col min-h-full justify-center px-4 sm:px-6 py-10 lg:px-8 bg-gradient-to-b from-sky-50 via-slate-50 to-slate-100">
                    <Stepper level={level} />
                    <div className="mx-auto w-full max-w-sm">
                        <Card isBorder={true} title={"Verify Your Mobile"}>
                            <div className="my-5 sm:mx-auto sm:w-full sm:max-w-sm">
                                <div className="space-y-6">
                                    <div className="rounded-xl bg-sky-50 border border-sky-100 p-4 text-center">
                                        <label className="block text-xs font-medium text-gray-500 mb-1">OTP sent to</label>
                                        <h1 className="text-2xl sm:text-3xl flex flex-wrap justify-center items-center gap-2 text-sky-700 font-semibold">
                                            <PiPhone /><span>{mobileNumber}</span>
                                            <button onClick={() => setLevel(1)}
                                                className='text-sky-600 cursor-pointer hover:text-sky-500 text-sm underline underline-offset-2'>
                                                Edit
                                            </button>
                                        </h1>
                                    </div>
                                    <div>
                                        <label htmlFor="otp" className="block text-sm font-medium leading-6 text-gray-900">
                                            OTP
                                        </label>
                                        <div className="mt-2">
                                            <Input placeholder="Enter OTP"
                                                id="otp"
                                                type="tel"
                                                inputMode="numeric"
                                                value={otp}
                                                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                                                Icon={<PiPassword className="text-green-600 text-lg" />} />
                                        </div>
                                    </div>
                                    <div>
                                        {lodingCrud ?
                                            <LodingButton text={"Verifing otp.."} />
                                            :
                                            <button className="btn-blue w-full" onClick={VerifyOtpMobile}>
                                                Verify OTP
                                            </button>
                                        }
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            }

            {/* ---------------- LEVEL 3 : Details form ---------------- */}
            {level === 3 &&
                <div className='w-full bg-gradient-to-b from-sky-50 via-slate-50 to-slate-100 px-2 sm:px-4 py-8 lg:flex lg:flex-col lg:items-center'>
                    <Stepper level={level} />
                    <div className='w-full lg:w-8/12 xl:w-7/12'>
                        <Card title={"Create An Account"} isBack={true} isBorder={true}>
                            <form onSubmit={handleSubmit}>
                                <div className="p-4 sm:p-6 space-y-6">
                                    <div className="grid grid-cols-12 gap-4 sm:gap-6">
                                        <div className="col-span-12">
                                            <div className="rounded-xl bg-green-50 border border-green-100 p-3 text-center">
                                                <h1 className="text-xl sm:text-2xl flex flex-wrap justify-center items-center gap-2 text-green-600 font-semibold">
                                                    <MdOutlineVerifiedUser />
                                                    <PiPhone /><span>{mobileNumber}</span>
                                                    <span className="text-xs bg-green-600 text-white px-2 py-0.5 rounded-full">Verified</span>
                                                    <button type="button" onClick={() => setLevel(1)}
                                                        className='text-sky-700 cursor-pointer hover:text-sky-500 text-sm underline underline-offset-2'>
                                                        Edit
                                                    </button>
                                                </h1>
                                            </div>
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label htmlFor="fullName" className="block mb-2 text-sm font-medium text-gray-900">Full Name</label>
                                            <Input placeholder="Enter Your Full Name"
                                                id="fullName"
                                                Icon={<FaUserDoctor className='text-green-500' />}
                                                value={values.fullName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isError={errors.fullName && touched.fullName}
                                            />
                                            {errors.fullName && touched.fullName && <p className='text-red-600 text-xs mt-1'>{errors.fullName}</p>}
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900">
                                                Password <span className='text-green-700 text-[9px]'>(Please remember it for future reference)</span>
                                            </label>
                                            <Input placeholder="Enter Password eg: Abcd@123"
                                                id="password"
                                                type="password"
                                                Icon={<RiLockPasswordFill className="text-green-500 h-4 w-4" />}
                                                value={values.password}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isError={errors.password && touched.password}
                                            />
                                            {errors.password && touched.password && <p className='text-red-600 text-xs mt-1'>{errors.password}</p>}
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label htmlFor="specialisationId" className="block mb-2 text-sm font-medium text-gray-900">Specialisation</label>
                                            <Select
                                                Icon={<MdMedicalInformation className='text-cyan-500 text-lg' />}
                                                id="specialisationId"
                                                value={values.specialisationId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}>
                                                <option value="">Select Specialisation...</option>
                                                {specialisationData.map((items) =>
                                                    <option key={items.id} value={items.id}>{items.name}({items.shortName})</option>
                                                )}
                                            </Select>
                                            {errors.specialisationId && touched.specialisationId && <p className='text-red-600 text-xs mt-1'>{errors.specialisationId}</p>}
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label htmlFor="degree" className="block mb-2 text-sm font-medium text-gray-900">Degree</label>
                                            <Input placeholder="Enter Degree eg: MBBS, MD"
                                                id="degree"
                                                Icon={<FaUserGraduate className='text-blue-500 text-md' />}
                                                type="text"
                                                value={values.degree}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isError={errors.degree && touched.degree}
                                            />
                                            {errors.degree && touched.degree && <p className='text-red-600 text-xs mt-1'>{errors.degree}</p>}
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label htmlFor="experiance" className="block mb-2 text-sm font-medium text-gray-900">Experience</label>
                                            <Input placeholder="Enter Your Experience (in years)"
                                                id="experiance"
                                                Icon={<FaBriefcaseMedical className='text-yellow-500 text-md' />}
                                                type="text"
                                                value={values.experiance}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isError={errors.experiance && touched.experiance}
                                            />
                                            {errors.experiance && touched.experiance && <p className='text-red-600 text-xs mt-1'>{errors.experiance}</p>}
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label htmlFor="gender" className="block mb-2 text-sm font-medium text-gray-900">Gender</label>
                                            <Select
                                                Icon={<BsGenderAmbiguous className='text-orange-600 text-lg' />}
                                                id="gender"
                                                value={values.gender}
                                                onChange={handleChange}
                                                onBlur={handleBlur}>
                                                {GetEGender.EGender.map((items) =>
                                                    <option key={items.Id} value={items.Id}>{items.Name}</option>
                                                )}
                                            </Select>
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label className="block mb-2 text-sm font-medium text-gray-900">State</label>
                                            {!lodingStateCrud ?
                                                <Select
                                                    Icon={<FaMapLocationDot className='text-green-600 text-lg' />}
                                                    id="stateId"
                                                    value={values.stateId}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}>
                                                    <option value="">Select state...</option>
                                                    {stateData.map((item) =>
                                                        <option key={item.id} value={item.id}>{item.name} ({item.shortName})</option>
                                                    )}
                                                </Select>
                                                : <Select disabled
                                                    Icon={<FaMapLocationDot className='text-green-600 text-lg' />} >
                                                    <option>Loading states...</option>
                                                </Select>
                                            }
                                            {errors.stateId && touched.stateId && <p className='text-red-600 text-xs mt-1'>{errors.stateId}</p>}
                                        </div>

                                        <div className="col-span-12 sm:col-span-6">
                                            <label className="block mb-2 text-sm font-medium text-gray-900">City</label>
                                            {!lodingCityCrud ?
                                                <Select
                                                    id="cityId"
                                                    Icon={<FaLocationDot className='text-brand-600' />}
                                                    value={values.cityId}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}>
                                                    <option value="">Select city...</option>
                                                    {cityData.map((item) =>
                                                        <option key={item.id} value={item.id}>{item.name} ({item.shortName})</option>
                                                    )}
                                                </Select>
                                                : <Select disabled
                                                    Icon={<FaLocationDot className='text-brand-600' />} >
                                                    <option>Loading cities...</option>
                                                </Select>
                                            }
                                            {errors.cityId && touched.cityId && <p className='text-red-600 text-xs mt-1'>{errors.cityId}</p>}
                                        </div>

                                        <div className="col-span-12">
                                            <label htmlFor="address" className="block mb-2 text-sm font-medium text-gray-900">Address</label>
                                            <Input placeholder="Enter Your Clinic / Hospital Address"
                                                id="address"
                                                Icon={<FaAddressCard className='text-brand-500 text-md' />}
                                                value={values.address}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                isError={errors.address && touched.address}
                                            />
                                            {errors.address && touched.address && <p className='text-red-600 text-xs mt-1'>{errors.address}</p>}
                                        </div>

                                        {/* ------- Terms & Conditions agreement ------- */}
                                        <div className="col-span-12">
                                            <div className={`rounded-xl border p-4 flex items-start gap-3 transition-colors
                                                ${agreedTerms ? "bg-green-50 border-green-200" : "bg-sky-50 border-sky-100"}`}>
                                                <input
                                                    type="checkbox"
                                                    id="agreeTerms"
                                                    checked={agreedTerms}
                                                    onClick={onTermsCheckboxClick}
                                                    onChange={() => { }}
                                                    className="mt-1 h-4 w-4 shrink-0 accent-sky-600 cursor-pointer"
                                                />
                                                <label htmlFor="agreeTerms" className="text-sm text-gray-700 cursor-pointer select-none"
                                                    onClick={onTermsCheckboxClick}>
                                                    I have read and agree to the{" "}
                                                    <button type="button"
                                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowTerms(true) }}
                                                        className="text-sky-600 font-semibold underline underline-offset-2 hover:text-sky-500">
                                                        Terms &amp; Conditions
                                                    </button>{" "}
                                                    of Doctora — Digital OPD.
                                                    {!agreedTerms &&
                                                        <span className="block text-[11px] text-gray-500 mt-0.5">
                                                            (You must open and read the full terms before you can accept.)
                                                        </span>
                                                    }
                                                    {agreedTerms &&
                                                        <span className="flex items-center gap-1 text-[11px] text-green-600 mt-0.5 font-medium">
                                                            <IoCheckmarkDoneCircleOutline /> Terms accepted
                                                        </span>
                                                    }
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center p-4 sm:p-6 space-x-3 rtl:space-x-reverse border-t border-gray-200 rounded-b">
                                    {!lodingCrud ?
                                        <button type="submit"
                                            className={(!isValid || !agreedTerms) ? "btn-blue-disabled w-full sm:w-auto" : "btn-blue w-full sm:w-auto"}
                                            disabled={!isValid || !agreedTerms}>
                                            Register
                                        </button>
                                        : <LodingButton text="Creating account.." />
                                    }
                                </div>
                            </form>
                        </Card>
                    </div>
                </div>
            }

            {level === 4 &&
                <UserRegisterCompleted />
            }
        </MasterPage>
    )
}